import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import NorthStar from "./NorthStar";
import Button from "./Button";
import { NAV_LINKS } from "../lib/data";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.7, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-[100] transition-colors duration-500 ${
          scrolled ? "bg-ink/80 backdrop-blur-xl border-b border-charcoal" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <nav className="mx-auto max-w-[1600px] px-6 sm:px-10 h-20 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
            <NorthStar className="w-4 h-4 text-warm" />
            <span className="font-sans text-sm tracking-[0.28em] uppercase text-warm">
              NSTR&nbsp;Talent
            </span>
          </a>

          <div className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="label link-underline !text-silver hover:!text-warm transition-colors duration-500"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <Button as="a" href="#brand-inquiry" variant="primary" className="!px-6 !py-3" data-testid="nav-work-with-us">
              Work With NSTR
            </Button>
          </div>

          <button
            className="md:hidden text-warm"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-menu-open"
          >
            <Menu className="w-6 h-6" />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[110] bg-ink flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-charcoal">
              <span className="font-sans text-sm tracking-[0.28em] uppercase text-warm">NSTR Talent</span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="nav-menu-close">
                <X className="w-6 h-6 text-warm" />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-6 gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                  className="font-serif text-5xl text-warm py-2"
                >
                  {l.label}
                </motion.a>
              ))}
              <Button
                as="a"
                href="#brand-inquiry"
                onClick={() => setOpen(false)}
                variant="primary"
                className="mt-8 w-full"
                data-testid="mobile-work-with-us"
              >
                Work With NSTR
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
